import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import StayInTheLoop from './StayInTheLoop';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* CTA Banner */}
        <div className="text-center mb-12 pb-10 border-b border-gray-700">
          <h3 className="text-2xl font-bold font-sans mb-3 text-white">Have a project in mind?</h3>
          <p className="text.subtle mb-6 max-w-lg mx-auto">
            Get a free launch blueprint in minutes, or book a call and we'll talk it through.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link
              to="/intake"
              className="inline-flex items-center gap-2 bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-purple-700 transition-all shadow-md hover:shadow-lg"
            >
              Get my free blueprint <ArrowRight className="w-4 h-4" />
            </Link>
            {/* `text.heading` (dot, not dash) was an undefined class, so this
                inherited near-black text on the near-black footer — an
                invisible button on every page. See fc6fbc5. */}
            <Link
              to="/book"
              className="inline-flex items-center gap-2 border border-gray-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-800 hover:border-purple-400 transition-all"
            >
              Book a free call
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-2xl font-bold font-sans mb-2 text-purple-300">Utopian Creations</h3>
            <p className="text-sm text.subtle mb-4">Bay Area developer helping small businesses get online and grow.</p>
            <p className="mb-6 leading-relaxed text.secondary">
              Websites, apps, and AI tools — built from scratch, not from templates.
            </p>
            <div className="flex space-x-4">
              {/* LinkedIn */}
              <a
                href="https://linkedin.com/company/utopian-creations"
                target="_blank"
                rel="noopener noreferrer"
                className="text.subtle hover:text-purple-300 transition-colors"
                aria-label="Follow us on LinkedIn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              {/* GitHub */}
              <a
                href="https://github.com/utopiancreations"
                target="_blank"
                rel="noopener noreferrer"
                className="text.subtle hover:text-purple-300 transition-colors"
                aria-label="View our GitHub"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold font-sans mb-4 text-purple-300">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text.secondary hover:text-purple-300 transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/portfolio" className="text.secondary hover:text-purple-300 transition-colors">Portfolio</Link>
              </li>
              <li>
                <Link to="/services" className="text.secondary hover:text-purple-300 transition-colors">Services</Link>
              </li>
              <li>
                <Link to="/specials" className="text.secondary hover:text-purple-300 transition-colors">Specials</Link>
              </li>
              <li>
                <a href="/same-day-launch/" className="text.secondary hover:text-purple-300 transition-colors">Same-Day Launch</a>
              </li>
              <li>
                <Link to="/book" className="text.secondary hover:text-purple-300 transition-colors">Book a Call</Link>
              </li>
              <li>
                <Link to="/insights" className="text.secondary hover:text-purple-300 transition-colors">Blog</Link>
              </li>
              <li>
                <Link to="/contact" className="text.secondary hover:text-purple-300 transition-colors">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold font-sans mb-4 text-purple-300">Contact</h4>
            <div className="space-y-3">
              <div>
                <p className="text.secondary">
                  <span className="block font-medium text-purple-400">Text</span>
                  <a href="sms:+15106299022?body=Hi%20Josh%2C%20I%20want%20a%20Same-Day%20Launch.%20My%20business%20is%3A%20" className="hover:text-purple-300 transition-colors">(510) 629-9022</a>
                </p>
              </div>
              <div>
                <p className="text.secondary">
                  <span className="block font-medium text-purple-400">Email</span>
                  info@utopiancreations.one
                </p>
              </div>
              <div>
                <p className="text.secondary">
                  <span className="block font-medium text-purple-400">Location</span>
                  San Francisco Bay Area, CA
                </p>
              </div>
              <div>
                <p className="text.secondary">
                  <span className="block font-medium text-purple-400">Hours</span>
                  Texts answered same day, 6am to 9pm PT
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SMS opt-in. Lives in the footer so the consent path documented in
            our toll-free verification workflow is reachable from every page —
            a documented opt-in that can't be found on the site fails review. */}
        <div className="border-t border-gray-700 mt-10 pt-10 grid gap-8 md:grid-cols-2 md:items-start">
          <div>
            <h4 className="text-lg font-semibold font-sans mb-2 text-purple-300">Utopian Creations Updates</h4>
            <p className="text-sm text.secondary leading-relaxed">
              Our own SMS program — offers, promotions, and news about Utopian Creations, sent from toll-free
              +1&nbsp;(877)&nbsp;831-0042. Marketing messages only, and only to people who ask for them.
            </p>
          </div>
          <StayInTheLoop theme="dark" />
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text.subtle">
            &copy; {new Date().getFullYear()} Utopian Creations. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy-policy" className="text-sm text.subtle transition-colors hover:text-purple-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-sm text.subtle transition-colors hover:text-purple-300">
              Terms of Service
            </Link>
            {/* Plain <a>: /sms-policy.html is a static file (crawler-readable
                without JS — carrier verification reviewers fetch it raw). */}
            <a href="/sms-policy.html" className="text-sm text.subtle transition-colors hover:text-purple-300">
              SMS Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
